var _excluded = ["disabled", "edges", "errorMessage", "fullWidth", "helperText", "helperTextPlacement", "hideLabel", "hideSpinner", "inputProps", "label", "name", "onBlur", "onChange", "onKeyDown", "required", "showError", "type", "placeholder", "value", "variant"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import { AsYouType } from 'libphonenumber-js';
import InputAdornment from '@material-ui/core/InputAdornment';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import FormControl from '../FormControl';
var asYouType = new AsYouType('NZ');
var numberFormat = function numberFormat(value) {
  if (value) {
    var number = value.toString().replace(/[^.0-9]/g, '');
    if (number.indexOf('.') > 0) {
      number = number.split('.');
      number = number[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + "." + (number[1] ? number[1].substring(0, 2) : '');
    } else {
      number = number.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    return number;
  }
  return value;
};
export var phoneFormat = function phoneFormat(value) {
  asYouType.reset();
  asYouType.input(value);
  return asYouType.formattedOutput;
};
var useStyles = makeStyles(function (theme) {
  return {
    input: {
      margin: 0,
      '& input[type=number]::-webkit-inner-spin-button, * input[type=number]::-webkit-outer-spin-button': {
        // eslint-disable-next-line react/prop-types
        '-webkit-appearance': function webkitAppearance(_ref) {
          var hideSpinner = _ref.hideSpinner;
          return hideSpinner ? 'none' : null;
        }
      },
      '& input[type="date"]::-webkit-inner-spin-button, & input[type="date"]::-webkit-calendar-picker-indicator': {
        background: 'none',
        display: 'none',
        '-webkit-appearance': 'none'
      },
      '& input.MuiInputBase-inputAdornedEnd[type=time]::-webkit-calendar-picker-indicator': {
        position: 'absolute',
        right: 0,
        padding: 8,
        opacity: 0,
        zIndex: 2,
        cursor: 'pointer',
        display: 'block'
      },
      '& .MuiInputAdornment-filled.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel)': {
        marginTop: 0
      },
      '& .MuiInputBase-root ': {
        border: function border(_ref2) {
          var variant = _ref2.variant;
          return variant === 'filled' ? "2px solid " + theme.palette.grey.transparent : null;
        },
        borderRadius: function borderRadius(_ref3) {
          var edges = _ref3.edges;
          switch (edges) {
            case 'rounded':
              return 99;
            case 'sharp':
              return 0;
            default:
              return theme.shape.borderRadius;
          }
        }
      },
      '& .MuiFilledInput-root': {
        backgroundColor: theme.palette.grey.transparent
      },
      '& .Mui-focused': {
        border: function border(_ref4) {
          var variant = _ref4.variant,
            required = _ref4.required;
          return variant === 'filled' ? "2px solid " + (required ? theme.palette.secondary.main : null) : null;
        }
      },
      '& .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: function borderColor(_ref5) {
          var value = _ref5.value,
            required = _ref5.required,
            showError = _ref5.showError;
          return required && !value || showError ? theme.palette.secondary.main : null;
        }
      },
      '& .MuiFilledInput-root.Mui-focused': {
        borderColor: function borderColor(_ref6) {
          var value = _ref6.value,
            required = _ref6.required,
            showError = _ref6.showError;
          return required && !value || showError ? theme.palette.secondary.main : 'transparent';
        }
      },
      '& input': {
        '&::placeholder': {
          color: theme.palette.grey.main,
          opacity: 1
        },
        padding: theme.spacing(3, 4)
      }
    }
  };
});
var TextInput = forwardRef(function (_ref7, ref) {
  var disabled = _ref7.disabled,
    edges = _ref7.edges,
    errorMessage = _ref7.errorMessage,
    fullWidth = _ref7.fullWidth,
    helperText = _ref7.helperText,
    helperTextPlacement = _ref7.helperTextPlacement,
    hideLabel = _ref7.hideLabel,
    hideSpinner = _ref7.hideSpinner,
    inputProps = _ref7.inputProps,
    label = _ref7.label,
    name = _ref7.name,
    onBlur = _ref7.onBlur,
    onChange = _ref7.onChange,
    onKeyDown = _ref7.onKeyDown,
    required = _ref7.required,
    showError = _ref7.showError,
    type = _ref7.type,
    placeholder = _ref7.placeholder,
    value = _ref7.value,
    variant = _ref7.variant,
    rest = _objectWithoutPropertiesLoose(_ref7, _excluded);
  var classes = useStyles({
    edges: edges,
    variant: variant,
    hideSpinner: hideSpinner,
    required: required,
    showError: showError,
    value: value
  });
  var formatValue = function formatValue(v) {
    switch (type) {
      case 'currency':
        return numberFormat(v);
      case 'tel':
        return phoneFormat(v);
      default:
        return v;
    }
  };
  var parseAsNumber = function parseAsNumber(v) {
    if (v.charAt(v.length - 1) === '.' || !v) {
      return v;
    }
    return parseFloat(v.replace(/,/g, ''));
  };
  var handleChange = function handleChange(e) {
    if (type === 'currency') {
      return onChange(parseAsNumber(numberFormat(e.target.value)));
    }
    if (type === 'tel') {
      e.target.value = formatValue(e.target.value);
      return onChange(e);
    }
    return onChange(e);
  };
  var handleBlur = function handleBlur(e) {
    if (type === 'currency' && e.target.value.charAt(e.target.value.length - 1) === '.') {
      onChange(parseAsNumber(e.target.value + "00"));
    }
    onBlur(e);
  };
  var handleKeyDown = function handleKeyDown(e) {
    if (type === 'number' && isNaN(e.key) && e.key !== 'Backspace' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'ArrowUp' && e.key !== 'ArrowDown' && (e.key !== '.' || e.key === '.' && (e.target.value === '' || e.target.value.includes('.')))) {
      e.preventDefault();
    }
    onKeyDown(e);
  };
  var getIcon = function getIcon() {
    return type === 'currency' ? {
      startAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
        position: "start"
      }, /*#__PURE__*/React.createElement(AttachMoneyIcon, null))
    } : null;
  };
  return /*#__PURE__*/React.createElement(FormControl, {
    fullWidth: fullWidth,
    name: name,
    label: label,
    helperText: helperText,
    helperTextPlacement: helperTextPlacement,
    disabled: disabled,
    required: required,
    showError: showError,
    errorMessage: errorMessage,
    edges: edges,
    hideLabel: hideLabel
  }, /*#__PURE__*/React.createElement(TextField, _extends({
    ref: ref,
    name: name,
    label: null,
    margin: "normal",
    size: "small",
    className: classes.input,
    variant: variant,
    disabled: disabled,
    value: value ? formatValue(value) : '',
    type: type === 'currency' ? 'text' : type,
    inputProps: _extends({
      inputMode: type === 'number' || type === 'currency' ? 'decimal' : type,
      'aria-label': label,
      autoComplete: type === 'password' ? 'new-password' : 'off'
    }, inputProps),
    placeholder: placeholder
    // eslint-disable-next-line react/jsx-no-duplicate-props
    ,
    InputProps: getIcon(),
    onChange: handleChange,
    onBlur: handleBlur,
    onKeyDown: handleKeyDown
  }, rest)));
});
TextInput.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * If `true`, the `input` element will be disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Make button edges rounded or square
   */
  edges: PropTypes.oneOf(['normal', 'rounded', 'sharp']),
  /**
   * The error message to display if showError === true
   */
  errorMessage: PropTypes.string,
  /**
   * If `true`, the input will take up the full width of its container.
   */
  fullWidth: PropTypes.bool,
  /**
   * The helper text content.
   */
  helperText: PropTypes.string,
  /**
   * Placed the helper text above or below the input.
   */
  helperTextPlacement: PropTypes.oneOf(['above', 'below']),
  /**
   * Make label visible only to screen readers
   */
  hideLabel: PropTypes.bool,
  /**
   * Whether or not to show a spiner on number inputs
   */
  hideSpinner: PropTypes.bool,
  /**
   * Attributes applied to the input element. When native is true, the attributes are applied on the select element
   */
  inputProps: PropTypes.object,
  /**
   * @ignore
   */
  onBlur: PropTypes.func,
  /**
   * @ignore
   */
  onChange: PropTypes.func,
  /**
   * @ignore
   */
  onKeyDown: PropTypes.func,
  /**
   * The label content.
   */
  label: PropTypes.string,
  /**
   * The field name.
   */
  name: PropTypes.string.isRequired,
  /**
   * The short hint displayed in the input before the user enters a value.
   */
  placeholder: PropTypes.string,
  /**
   * If `true`, the label is displayed as required and the `input` element` will be required.
   */
  required: PropTypes.bool,
  /**
   * Whether or not to display an error message (if there is a message to display).
   */
  showError: PropTypes.bool,
  /**
   * The input type attribute.
   */
  type: PropTypes.oneOf(['color', 'currency', 'email', 'number', 'password', 'search', 'tel', 'text', 'time', 'url']),
  /**
   * The value of the `input` element, required for a controlled component.
   */
  value: PropTypes.any,
  /**
   * The variant to use.
   */
  variant: PropTypes.oneOf(['filled', 'outlined'])
} : {};
TextInput.defaultProps = {
  disabled: false,
  edges: 'normal',
  errorMessage: '',
  fullWidth: false,
  helperText: '',
  helperTextPlacement: 'above',
  hideLabel: false,
  hideSpinner: true,
  inputProps: {},
  label: '',
  onBlur: function onBlur() {
    return null;
  },
  onChange: function onChange() {
    return null;
  },
  onKeyDown: function onKeyDown() {
    return null;
  },
  placeholder: '',
  required: false,
  showError: false,
  type: 'text',
  value: undefined,
  variant: 'outlined'
};
export default TextInput;